import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date as relative time (e.g., "13m ago", "2h ago", "3d ago")
 */
export function getRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now.getTime() - then.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 30) {
    return `${diffDays}d ago`;
  } else {
    return then.toLocaleDateString();
  }
}

/**
 * Safely parse a URL string, auto-adding https:// if missing
 * Blocks dangerous protocols (javascript:, data:, file:)
 * @returns URL object or null if invalid
 */
export function safeParseUrl(url: string): URL | null {
  if (!url || typeof url !== 'string') return null;

  const trimmed = url.trim();
  if (!trimmed) return null;

  // Block dangerous protocols
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('file:')) {
    return null;
  }

  // Auto-add https:// if no protocol
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    return new URL(withProtocol);
  } catch {
    return null;
  }
}

/**
 * Get hostname from URL string, with safe fallbacks
 * @returns hostname if valid URL, otherwise truncated original string
 */
export function getUrlHostname(url: string): string {
  if (!url || typeof url !== 'string') return '';

  const parsed = safeParseUrl(url);
  if (parsed) return parsed.hostname;

  // Fallback: truncate original string
  const trimmed = url.trim();
  return trimmed.length > 30 ? trimmed.substring(0, 30) + '...' : trimmed;
}

/**
 * Expand social media handles to full URLs
 * @param value - user input (could be handle, partial URL, or full URL)
 * @param platform - 'x' for Twitter/X, 'telegram' for Telegram
 * @returns full URL or original value if already a URL
 */
export function expandSocialHandle(
  value: string,
  platform: 'x' | 'telegram'
): string {
  if (!value || !value.trim()) return value;

  const trimmed = value.trim();

  // Already a full URL? Leave it alone
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  // Extract handle (remove @ if present)
  const handle = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;

  // If empty after removing @, return original
  if (!handle) return value;

  // If it looks like a domain (has dots), add protocol only
  if (handle.includes('.')) {
    return `https://${handle}`;
  }

  // Expand to full URL based on platform
  switch (platform) {
    case 'x':
      return `https://x.com/${handle}`;
    case 'telegram':
      return `https://t.me/${handle}`;
    default:
      return value;
  }
}
